declare global {
  interface Window {
     imperium_qualityscore?: {
      CollectData: (PageId: any, matrixData: any, callback: (responseData: any) => void) => void;
    };
    realAnswerQuestions?: {
      isBrandQuestio: string
    };
  }
}
import { Component, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Params } from '@angular/router';
import { Memoize } from 'lodash-decorators';
import { BehaviorSubject, combineLatest, Observable, Subject, Subscription } from 'rxjs';
import { filter, map, take, takeUntil, tap } from 'rxjs/operators';
import { UUID } from 'io-ts-types/lib/UUID';

import { filterRealValues, isNullOrUndefined, isRealValue, ProcedureTemplateType, ProcedureTemplateTypes } from '@tr-common';

import { REGISTRY_DOCUMENT } from '../../../global/tokens';
import { IProcedure, MinimalStudyType, procedureHasQuestions } from '../../../models';
import { LayoutService, ScriptService } from '../../../services';
import { DialogQuestionListComponent } from '../../dialogs/dialog-question-list/dialog-question-list.component';
import {
  ActiveQuestionCollection,
  composeURLForPromotionResearchStudy,
  composeURLForResearchStudy,
  DraftAnswer,
  DraftAnswersType,
  getAmountOfAbsentAnswers,
  QueryParamsIDs,
  SurveyIntroOrResearchPageType,
  TooltipClassType,
} from '../../models';
import {
  finishProcedure,
  NavigateActions,
  nextQuestion,
  nextUnansweredQuestionAfterSubmit,
  previousQuestion,
  returnToDashboard,
  selectActiveQuestion,
  selectParticipantDetails,
  storeRelevantIDResponse,
  StudyConsentFacade,
  StudyFacade,
} from '../../store';
import { QuestionComponent } from '../../components/question/question.component';
import { Store } from '@ngrx/store';
import { SurveyService } from '../../services/survey.service';

@Component({
  selector: 'lib-survey',
  templateUrl: './survey.component.html',
  styleUrls: ['./survey.component.scss'],
  // @ts-ignore
  providers: [{provide: REGISTRY_DOCUMENT, useValue: window.document}],
})
export class SurveyComponent implements OnDestroy {
  activeQuestion: string = undefined;
  activeProcedure$: Observable<IProcedure> = this.study.activeProcedure$;
  researchPage$ = this.study.researchPage$;
  answerSaveError$ = this.study.answerSaveError$;
  valid$ = new BehaviorSubject<boolean>(false);
  currentProcedure: ProcedureTemplateType;
  draftAnswers: DraftAnswersType = [];
  showTooltip$ = new BehaviorSubject<TooltipClassType>(null);
  confirmation_was_showed = false;
  attempt_first = false;
  absentAnswers = 0;
  warningStatus: string | null
  consentPending$ = combineLatest([this.consent.consentPending$, this.study.fullStudyLoading$]).pipe(
    map(([consentPending, fullStudyLoading]) => consentPending || fullStudyLoading)
  );
  activeQuestion$: Observable<ActiveQuestionCollection> = combineLatest([
    this.study.activeQuestion$, this.layoutService.isMobileUsingLandscape$
  ]).pipe(
    tap(([collection]) => this.checkIfActiveQuestionSet(collection)),
    tap(([collection]) => this.resetValidState(collection)),
    map(([collection, mobileLandscape]) => ({...collection, mobileLandscape})),
    // tap(data => console.log({handler: 'activeQuestion$', ...data}))
  );
  loadModuleSettingsAndConsents$ = combineLatest([
    this.study.blockedNavigation$, this.study.study$, this.route.queryParams
  ]).pipe(
    map((
      [isBlocked, {id}, {procedure, questionId}]: [boolean, MinimalStudyType, Params]
    ) => ({isBlocked, studyID: id, procedure, questionId})),
    filter(({isBlocked, procedure}) => !isBlocked && isRealValue(procedure)),
    tap(({studyID, procedure, questionId}) => this.resetProcedureState(studyID, procedure, questionId)),
    tap(({questionId}) => this.resetParentQuestionState(questionId))
  );
  subscription = new Subscription();
  isMobile$ = this.layoutService.isMobile$;
  isMobileDevice:any;
  private destroy$ = new Subject<void>();
  isRequiredRelevantID: boolean;
  isTypeCoreStudy: boolean;
  isTypeAnnualStudy: boolean;
  imperiumClientID: string;
  constructor(
    public study: StudyFacade,
    public consent: StudyConsentFacade,
    public layoutService: LayoutService,
    public matDialog: MatDialog,
    private route: ActivatedRoute,
    private surveyService: SurveyService,
    public store: Store<any>,
  ) {
    this.subscription.add(this.loadModuleSettingsAndConsents$.subscribe());
    this.imperiumClientID = this.surveyService.imperiumClientId;
  }

  @Memoize() getUrl({url, userID, add_participant_id_query_param}: SurveyIntroOrResearchPageType): string {
    return composeURLForPromotionResearchStudy(url, userID, add_participant_id_query_param);
  }

  get hasUnsavedChanges(): boolean {
    return this.draftAnswers.length > 0;
  }

  ngOnInit(): void{
    this.layoutService.isMobile$.subscribe(isMobile => {
      this.isMobileDevice = isMobile;
    });

    if(this.imperiumClientID){
      this.subscription.add(
        combineLatest([
          this.store.select(selectActiveQuestion).pipe(filter(Boolean)),
          this.store.select(selectParticipantDetails).pipe(filter(Boolean)),// only proceed if details exist
          this.study.userStudy$,
          this.activeQuestion$
        ])
          .pipe(takeUntil(this.destroy$)) // ensure cleanup
          .subscribe(([currentQuestion, participant, userStudy, activeQuestion]) => {
            this.isTypeCoreStudy = userStudy.study_type === 'core_study';
            this.isTypeAnnualStudy = userStudy.study_type === 'annual_questionnaire';
            if (this.isTypeCoreStudy || this.isTypeAnnualStudy){
    
              let form = document.getElementById('qs-imp-form') as HTMLFormElement;
              if (!form) {
                form = document.createElement('form');
                form.id = 'qs-imp-form';
                form.style.display = 'none';
                document.body.appendChild(form);
              }
              this.isRequiredRelevantID = this.checkRequiredRelevantID(currentQuestion, activeQuestion)
              
              // Unix timestamp is added with the question ID to make a unique pageID
              // This is suggested by Dynata as they not allowed prev & back operations in survey
              const timestamp = Date.now();
              const uniquePageId = `${currentQuestion.id}-${timestamp}`;

              if(this.isRequiredRelevantID){
                this.addOrUpdateHiddenInput(form, 'GeoCodes', '1,US');
              } else{
                this.removeHiddenInput('GeoCodes');
              }
              this.addOrUpdateHiddenInput(form, 'SurveyID', userStudy?.study_id);
              this.addOrUpdateHiddenInput(form, 'ClientID', this.imperiumClientID);
              this.addOrUpdateHiddenInput(form, 'PanelistID', participant?.id);
              this.addOrUpdateHiddenInput(form, 'PageID', uniquePageId);
            }
          })
      );
    }
    
  }

  /**
 * Determines whether RelevantID validation is required for the current question.
 *
 * Logic:
 * - If there are no existing answers (first interaction), and:
 *    • It is a Core Study and the user is on the first Screening question, OR
 *    • It is an Annual Study and the user is on the first Questionnaire question,
 *   then RelevantID should be triggered.
 *
 * @param param0.order                The position of the current question.
 * @param param0.answers              Existing answers for the question (null/undefined if first attempt).
 * @param param0.procedure            The question procedure type (e.g., 'screening', 'questionnaire').
 * @param param1.isFirstQuestion      Indicates whether this is the first question for Annual studies.
 *
 * @returns true if RelevantID needs to be requested, otherwise false.
 * For Annual Questionniare isFirstQuestion is used to detect the first question
 */
  checkRequiredRelevantID({order, answers, procedure}: any, {isFirstQuestion}: any): boolean {
    if (isNullOrUndefined(answers) && ((this.isTypeCoreStudy && procedure === 'screening' && order === 1) || 
  (isFirstQuestion && this.isTypeAnnualStudy && procedure === 'questionnaire'))){
      return true;
    } else {
      return false;
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  clickOnWrapper = (): void => { this.showTooltip$.next(null); };

  scrollToTop(): void {
    if(this.isMobileDevice){
      const scrollableElement = document.querySelector('.lib-survey-wrapper') as HTMLElement;
      if (scrollableElement) {
        // Scroll to top of the element
        scrollableElement.scrollTo({
          top: 0,
          behavior: 'smooth' // Smooth scrolling behavior
        });
      }
    }
    
  }

  onAnswer({answers, validity, hasWarning}: DraftAnswer): void {
    // console.log({handler: 'onAnswer', answers, validity, hasWarning});
    this.warningStatus = null;
    this.showTooltip$.next(null);
    this.draftAnswers = answers;
    if (validity && isRealValue(this.study.showHint$.value)) {
      this.study.showHint$.next(false);
    }
    if(hasWarning){
      this.warningStatus = 'INVALID-WARNING'
      this.valid$.next(hasWarning);
    }
    this.valid$.next(validity);
  }

  toQuestions(): void {
    this.matDialog.open<DialogQuestionListComponent, void, string>(
      DialogQuestionListComponent, {maxWidth: '100vw', panelClass: 'question-list', hasBackdrop: true}
    ).afterClosed().pipe(take(1), filterRealValues()).subscribe(id => this.study.navigateToQuestion(id));
  }

  // @ts-ignore
  toNextQuestion(activeQuestion: ActiveQuestionCollection, event: MouseEvent, libQuestion: QuestionComponent): void {
    this.scrollToTop();
    const draftAnswers = [...this.draftAnswers];
    const isNotValid = !(this.valid$.value && libQuestion.isValidByTreeControlState);
    const {previewMode, isLastQuestion} = activeQuestion;
    const preview = previewMode ? previewMode : undefined;
    const navigate: NavigateActions = isLastQuestion
      ? finishProcedure
      : this.absentAnswers > 1 ? nextUnansweredQuestionAfterSubmit : nextQuestion;
    const tooltipClass: TooltipClassType = isNotValid
      ? 'invalid-state'
      : (!isNotValid && activeQuestion.parentQuestion.needConfirmationWarning(!this.confirmation_was_showed))
        ? 'confirmation-warning'
        : null;
    const warningMessage = (this.warningStatus === 'INVALID-WARNING' && !this.attempt_first) ? 'show-warning' : null;

    event.stopPropagation();
    this.study.showHint$.next(isNotValid);
    this.showTooltip$.next(tooltipClass);
    // console.log({handler: 'toNextQuestion', tooltipClass, draftAnswers, isNotValid, navigate});
    if (draftAnswers.length > 0 && !isNotValid) {
      if (tooltipClass === 'confirmation-warning') {
        this.confirmation_was_showed = true;
      } else if (warningMessage === 'show-warning') {
        this.attempt_first = true;
      } else {
        this.attempt_first = false;
        this.draftAnswers = [];
        this.collectDataService(draftAnswers);
        this.study.saveAnswers(draftAnswers, navigate, preview);
      }
    } else if (isNotValid) {
      console.warn('Question logic is not valid');
    } else {
      this.study.nextQuestion(activeQuestion.isLastQuestion, preview);
    }
  }

  toPreviousQuestion(activeQuestion: ActiveQuestionCollection): void {
    this.scrollToTop();
    const draftAnswers = [...this.draftAnswers];
    let preview = activeQuestion.previewMode ? activeQuestion.previewMode : undefined;
    // The block below was added because of the procedure status autodetect and detect unanswered question engines,
    // so we turn preview mode on automatically if all the questions have answers
    if (activeQuestion.isLastQuestion && !activeQuestion.unansweredQuestions && isNullOrUndefined(preview)) {
      preview = true;
    }
    this.study.showHint$.next(false);
    if (draftAnswers.length > 0 && this.valid$.value) {
      this.draftAnswers = [];
      this.collectDataService(draftAnswers);
      this.study.saveAnswers(draftAnswers, previousQuestion, activeQuestion.isLastQuestion || preview);
    } else {
      this.study.previousQuestion(preview);
    }
  }

  backToDashboardOrSubmit({previewMode}: ActiveQuestionCollection): void {
    const draftAnswers = [...this.draftAnswers];

    this.draftAnswers = [];
    if (draftAnswers.length > 0 && this.valid$.value) {
      this.study.saveAnswers(draftAnswers, previewMode ? finishProcedure : returnToDashboard, previewMode);
    } else {
      this.study.returnToDashboard(previewMode);
    }
  }

  toQuestion(id: string): void {
    this.study.navigateToQuestion(id);
  }

  hideTooltip(): void {
    this.showTooltip$.next(null);
  }

  private checkIfActiveQuestionSet({activeQuestionId}: ActiveQuestionCollection): void {
    const {questionId} = this.route.snapshot.queryParams as QueryParamsIDs;

    if (isRealValue(questionId) && isNullOrUndefined(activeQuestionId)) {
      this.study.setActiveQuestion(questionId);
    }
    // console.log({handler: 'checkIfActiveQuestionSet', questionId, activeQuestionId});
  }

  private resetValidState({activeQuestionId, questionAnswers}: ActiveQuestionCollection): void {
    const questionAnswer = isRealValue(questionAnswers) ? questionAnswers[activeQuestionId] : null;

    this.valid$.next(isRealValue(questionAnswer));
  }

  private resetProcedureState(studyID: UUID | string, procedure: ProcedureTemplateType, questionId: string): void {
    if (isRealValue(procedure)  && this.currentProcedure !== procedure) {
      this.currentProcedure = procedure;
      this.study.setActiveProcedure(procedure);
      this.study.loadModuleSettings(studyID, procedure, true);
      if (procedure === ProcedureTemplateTypes.consent) {
        this.consent.loadConsents();
      } else if (procedureHasQuestions(procedure) && isNullOrUndefined(questionId)) {
        this.study.preselectQuestion();
      }
    }
  }

  private resetParentQuestionState(questionId: string): void {
    if (isRealValue(questionId) && questionId !== this.activeQuestion) {
      this.activeQuestion = questionId;
      this.showTooltip$.next(null);
      this.confirmation_was_showed = false;
      this.draftAnswers = [];
      this.absentAnswers = getAmountOfAbsentAnswers();
      this.study.resetAnswersSaveError();
      this.layoutService.scrollIt$.next(true);
      this.study.showHint$.next(null);
      this.study.setActiveQuestion(questionId);
    }
  }

  collectDataService(matrixDataPoints?: any) {
    if (
      (this.isTypeCoreStudy || this.isTypeAnnualStudy) &&
      typeof window.imperium_qualityscore !== "undefined" &&
      window.imperium_qualityscore?.CollectData &&
      this.imperiumClientID
    ) {
      const pageId = (document.getElementById('PageID') as HTMLInputElement)?.value;
      let study_type = this.isTypeCoreStudy ? 'core_study' : this.isTypeAnnualStudy ? 'annual_questionnaire' : '';
      let matrixData = '';
      const isMatrixScale = matrixDataPoints.every(item => item.widget === 'matrix_scale');
      if (matrixDataPoints && isMatrixScale) {
        // Group by question
        const grouped: Record<string, string[]> = matrixDataPoints.reduce((acc, item) => {
          if (!acc[item.question]) acc[item.question] = [];
          acc[item.question].push(`${this.convertUuid(item.question_option)}-C${item.value + 1}`);
          return acc;
        }, {} as Record<string, string[]>);
  
        // Build formatted string
        matrixData = Object.entries(grouped)
          .map(([question, opts]) => `${this.convertUuid(question)}:${opts.join(";")}`)
          .join(";");
      }
  
      const apiTimeout = setTimeout(() => {
        this.store.dispatch(
          storeRelevantIDResponse({
            isResponseAvailable: false,
            data: null,
            failureReason: "No response received from QualityScore",
            studyType: study_type
          })
        );
      }, 5000); // 5 seconds
      const isGeoCodeExist = document.getElementById("GeoCodes");
      try {
        window.imperium_qualityscore?.CollectData(pageId, matrixData, (response) => {
          clearTimeout(apiTimeout);
  
  
          if (response?.StatusCode === 401) {
            if (isGeoCodeExist) {
              this.store.dispatch(
                storeRelevantIDResponse({
                  isResponseAvailable: false,
                  data: null,
                  failureReason: "ERROR - an issue with ClientID",
                  studyType: study_type
                })
              );
            }
          } else if (response?.StatusCode === 200) {
            if (isGeoCodeExist) {
              this.store.dispatch(
                storeRelevantIDResponse({
                  isResponseAvailable: true,
                  data: response,
                  failureReason: "",
                  studyType: study_type
                })
              );
            }
          }
        });
      } catch (e) {
        clearTimeout(apiTimeout);
        console.warn(`Unknown issue during call CollectData() from QualityScore: ${e}`);
        if (isGeoCodeExist) {
          this.store.dispatch(
            storeRelevantIDResponse({
              isResponseAvailable: false,
              data: null,
              failureReason: `Error during QualityScore call`, 
              studyType: study_type
            })
          );
        }
      }
    }
  }
  
  convertUuid(uuid) {
    return uuid.replace(/-/g, '_');
  }

  private addOrUpdateHiddenInput(form: HTMLFormElement, name: string, value: string): void {
    let input = document.getElementById(name) as HTMLInputElement;
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.id = name;
      form.appendChild(input);
    }
    input.value = value;
  }
  
  private removeHiddenInput(name: string): void {
    const input = document.getElementById(name);
    if (input) {
      input.remove();
    }
  }
  
}
